import { ArchiveIcon, ArchiveRestoreIcon, MailIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { AccessRequestActions } from '@/features/workspaces';
import { cn } from '@/lib/utils';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';
import {
  formatElapsedSince,
  formatExactTime,
  formatRelativeTime,
} from '@/shared/utils/time';

import {
  ACKNOWLEDGE_COPY,
  canAcknowledge,
  CRITICAL_LABEL,
  notificationReason,
  notificationSentence,
  RESOLVED_LABEL,
  triggerRepeatLine,
} from '../copy';
import type { InboxRow as Row } from '../groupByTask';
import { KIND_ICON, TONE_CLASS, toneOf } from '../kindIcon';

/**
 * One inbox row: the newest notification about a task, how many more there
 * are, its workspace and when (spec 131 FR-004), laid out as one grid
 * (spec 141): the kind's square, the text, and a right column that holds the
 * time — or, on hover and keyboard focus, the row's actions.
 *
 * The row is the way in. A row whose cause is over says so with a chip and a
 * dimmed square, and stops asking for attention (spec 135).
 */
export function InboxRow({
  row,
  onOpen,
  onArchive,
  onUnarchive,
  onMarkUnread,
  onAcknowledge,
  selection,
}: {
  row: Row;
  onOpen: (row: Row) => void;
  /** Absent in the Archived view, where there is nothing left to archive. */
  onArchive?: (row: Row) => void;
  /** Offered in the notifications page's Archived view (spec 147). */
  onUnarchive?: (row: Row) => void;
  onMarkUnread?: (row: Row) => void;
  /** "I'm on it" (spec 138), offered while the latest still needs action. */
  onAcknowledge?: (row: Row) => void;
  /**
   * The notifications page's selection (spec 147 US2): a checkbox before the
   * row, outside its button, so Tab reaches it and Space toggles it.
   */
  selection?: { selected: boolean; onToggle: () => void };
}) {
  const phone = usePhoneWidth();
  const latest = row.latest;
  const unread = row.unread > 0;
  const resolved = latest.resolved_at != null;
  const critical = latest.severity === 'critical';
  const more = row.items.length - 1;
  const sentence = notificationSentence(latest);
  const reason = notificationReason(latest);
  const repeats =
    latest.kind === 'trigger_failed'
      ? triggerRepeatLine(latest.payload, latest.created_at)
      : null;
  const excerpt =
    (latest.kind === 'task_commented' || latest.kind === 'task_mentioned') &&
    latest.payload.comment_excerpt
      ? latest.payload.comment_excerpt
      : (latest.kind === 'scheduler_failed' ||
            latest.kind === 'scheduler_stalled') &&
          latest.payload.reason
        ? latest.payload.reason
        : null;
  const meta = [
    latest.workspace.name ?? 'Workspace',
    more > 0
      ? `${String(more)} more ${more === 1 ? 'update' : 'updates'}`
      : null,
    reason,
  ]
    .filter(Boolean)
    .join(' · ');
  const Icon = KIND_ICON[latest.kind];
  // Mark unread only makes sense once read; Archive is offered on every row
  // outside the Archived view (spec 141 US3).
  const markUnread = !unread ? onMarkUnread : undefined;
  const hasActions = Boolean(onArchive ?? onUnarchive ?? markUnread);
  const acknowledge =
    onAcknowledge && canAcknowledge(latest) ? onAcknowledge : undefined;
  // A request to join is answered where it is read (spec 190): Add or Dismiss,
  // until it is answered here, in Settings, or by another owner.
  // The row's state, as chips under its details. On a phone they sit on the
  // line the row's actions take anyway, so a resolved row is one line
  // shorter (spec 274).
  const chips =
    critical || resolved || latest.acknowledged_at ? (
      <span className="mt-1 flex flex-wrap items-center gap-1.5 max-md:mt-0">
        {critical ? (
          <span className="bg-destructive/10 text-destructive rounded px-1.5 text-2xs leading-[18px] font-medium">
            {CRITICAL_LABEL}
          </span>
        ) : null}
        {resolved ? (
          <span className="bg-fill-success/20 text-state-success rounded px-1.5 text-2xs leading-[18px] font-medium">
            {RESOLVED_LABEL}
          </span>
        ) : null}
        {latest.acknowledged_at ? (
          <span className="bg-muted text-muted-foreground rounded px-1.5 text-2xs leading-[18px] font-medium">
            {ACKNOWLEDGE_COPY.done}
          </span>
        ) : null}
      </span>
    ) : null;
  const lead = acknowledge ?? (phone ? chips : null);
  const requestId =
    latest.kind === 'workspace_access_requested' && !resolved
      ? (latest.payload.access_request_id ?? null)
      : null;

  return (
    <li
      className={cn(
        'group/row hover:bg-accent focus-within:bg-accent relative rounded-lg transition-colors',
        // On a phone the row's actions take a line of their own, beside "I'm
        // on it" when there is one (spec 265): always shown there, at 44px,
        // laid over the row they covered its details and its Resolved chip.
        'max-md:flex max-md:flex-wrap max-md:items-center',
        selection ? 'ps-8' : '',
        selection?.selected ? 'bg-primary/5' : '',
      )}
    >
      {selection ? (
        // The whole gutter is the target, not just the 16px box (spec 185): a
        // native checkbox takes no larger hit area of its own, so a label the
        // height of the row, wrapping it, catches the tap. On touch it also
        // takes the row's own left padding, 42px wide (spec 274).
        <label className="absolute inset-y-0 left-0 w-8 cursor-pointer pointer-coarse:w-10.5">
          {/* Level with the kind's square: its centre is 26px down the row. */}
          <input
            type="checkbox"
            aria-label={`Select: ${sentence}`}
            checked={selection.selected}
            onChange={selection.onToggle}
            className="accent-primary focus-visible:ring-ring absolute top-[18px] left-2.5 size-4 rounded-sm focus-visible:ring-3 focus-visible:outline-none"
          />
        </label>
      ) : null}
      <button
        type="button"
        onClick={() => {
          onOpen(row);
        }}
        className={cn(
          'focus-visible:ring-ring grid w-full grid-cols-[2rem_minmax(0,1fr)_auto] gap-x-2.5 rounded-lg p-2.5 text-left focus-visible:ring-3 focus-visible:outline-none',
          lead || requestId ? 'pb-1' : '',
          // On a phone the actions' own line follows, so the button's bottom
          // padding is the same 4px as above "I'm on it".
          hasActions ? 'max-md:pb-1' : '',
        )}
      >
        <span
          aria-hidden="true"
          className={cn(
            'relative grid size-8 place-items-center rounded-lg',
            TONE_CLASS[toneOf(latest)],
            resolved ? 'opacity-60 grayscale' : '',
          )}
        >
          <Icon className="size-4" strokeWidth={1.75} />
          {unread ? (
            <span className="bg-primary ring-popover absolute -top-0.5 -left-0.5 size-2.5 rounded-full ring-2" />
          ) : null}
        </span>
        {/* `pt-1.5` puts the first line's centre on the square's centre
            (20px line in a 32px square); the time below uses the same. */}
        <span className="flex min-w-0 flex-col gap-0.5 pt-1.5">
          <span
            className={cn(
              'line-clamp-2 text-sm leading-5',
              unread ? 'text-foreground font-medium' : 'text-foreground/80',
            )}
          >
            {sentence}
            {unread ? <span className="sr-only"> (unread)</span> : null}
          </span>
          {excerpt ? (
            <span className="border-border text-muted-foreground mt-0.5 line-clamp-2 border-l-2 ps-2 text-xs">
              {excerpt}
            </span>
          ) : null}
          {repeats ? (
            <span className="text-muted-foreground text-xs">{repeats}</span>
          ) : null}
          <span className="text-muted-foreground truncate text-xs" title={meta}>
            {meta}
          </span>
          {phone ? null : chips}
        </span>
        <span
          className="text-muted-foreground pt-1.5 text-xs leading-5 whitespace-nowrap tabular-nums"
          title={formatExactTime(latest.created_at)}
        >
          <span aria-hidden="true">
            {formatElapsedSince(latest.created_at)}
          </span>
          <span className="sr-only">
            {formatRelativeTime(latest.created_at)}
          </span>
        </span>
      </button>
      {lead ? (
        // Outside the row's button: one control inside another is invalid,
        // and this one must not also open the task. Indented to the text
        // column, so it reads as part of the row it sits in.
        <div className="ps-[3.25rem] pb-2.5 max-md:flex max-md:items-center max-md:gap-2 max-md:pb-1">
          {phone ? chips : null}
          {acknowledge ? (
            <Button
              variant="outline"
              size="xs"
              onClick={() => {
                acknowledge(row);
              }}
            >
              {ACKNOWLEDGE_COPY.action}
            </Button>
          ) : null}
        </div>
      ) : null}
      {requestId ? (
        // Outside the row's button, as "I'm on it" is.
        <div className="ps-[3.25rem] pb-2.5">
          <AccessRequestActions
            workspaceId={latest.workspace.id}
            requestId={requestId}
            name={latest.actor?.display_name ?? 'Someone'}
          />
        </div>
      ) : null}
      {hasActions ? (
        // Bottom-right, over the end of the details line, on the row's own
        // hover colour. Never on the sentence: in the time's place they covered
        // its end, and reserving their width there took the room the sentence
        // needs on every row. The details line is the one that may be covered,
        // and its full text is in its tooltip.
        <span className="bg-accent absolute right-2 bottom-1.5 flex gap-1 rounded-md max-md:static max-md:ms-auto max-md:bg-transparent max-md:pe-2 max-md:pb-1 max-md:opacity-100 opacity-0 transition-opacity group-focus-within/row:opacity-100 group-hover/row:opacity-100 pointer-coarse:opacity-100">
          {markUnread ? (
            <button
              type="button"
              aria-label={`Mark unread: ${sentence}`}
              title="Mark unread"
              onClick={() => {
                markUnread(row);
              }}
              className={ROW_ACTION}
            >
              <MailIcon aria-hidden="true" className="size-4" />
            </button>
          ) : null}
          {onArchive ? (
            <button
              type="button"
              aria-label={`Archive: ${sentence}`}
              title="Archive"
              onClick={() => {
                onArchive(row);
              }}
              className={ROW_ACTION}
            >
              <ArchiveIcon aria-hidden="true" className="size-4" />
            </button>
          ) : null}
          {onUnarchive ? (
            <button
              type="button"
              aria-label={`Unarchive: ${sentence}`}
              title="Unarchive"
              onClick={() => {
                onUnarchive(row);
              }}
              className={ROW_ACTION}
            >
              <ArchiveRestoreIcon aria-hidden="true" className="size-4" />
            </button>
          ) : null}
        </span>
      ) : null}
    </li>
  );
}

const ROW_ACTION =
  'text-muted-foreground hover:bg-background hover:text-foreground focus-visible:ring-ring touch-target grid size-7 place-items-center rounded-md focus-visible:ring-3 focus-visible:outline-none';
